/**
 * The practice fleet's way in (spec 53 section 5.3 step 2).
 *
 * Production's training page opens `https://training.ladeway.com/training/start#<ticket>`.
 * This runs at that address, before the app boots: there is no session yet,
 * so main.js would otherwise send the trainee to the login page. It posts the
 * ticket to `POST /api/auth/demo/lease {training}`, which leases or resumes
 * the trainee's own practice fleet and answers a session cookie and a CSRF
 * token exactly as the demo's lease does, then lands on `/training`.
 *
 * FROM A MODULE. When production's Training page sent them from a module, the
 * address names the module (`/training/start?module=assign#<ticket>`) and
 * they land on that module's page: its video, then Practice (spec 53 15.5;
 * Mark, 2026-11-03: watch first, then do it), rather than on the list to
 * find it again. `?practise=1`, which starts the practice at once, is still
 * honoured there (TrainingModule.vue) but no longer sent.
 *
 * The ticket travels in the URL's FRAGMENT, which no server sees: it stays
 * out of access logs and the Referer header. It leaves the address bar with
 * the redirect (location.replace), and stays there on a failure so a reload
 * retries.
 *
 * The demo's API refuses this route everywhere but the demo stack
 * (DemoSandboxesEnabled), so on production the page only ever says the link
 * did not work. And on the demo stack the host is the boundary (spec 53
 * 14.12): a ticket posted anywhere but training.ladeway.com answers 403
 * PRACTICE_HOST_REQUIRED, so a trainee is never leased their fleet on the
 * sales demo's name.
 */
export const START_PATH = '/training/start';
export const LANDING_PATH = '/training';

export function isTrainingStart(location = window.location) {
  return location.pathname.replace(/\/+$/, '') === START_PATH;
}

/** The ticket in the fragment, or null when it is not one (base64url only). */
export function ticketFrom(location = window.location) {
  const ticket = String(location.hash || '').replace(/^#/, '');
  return /^[A-Za-z0-9_-]{16,8192}$/.test(ticket) ? ticket : null;
}

/** The module named in the query, or null when there is none or it is not an id. */
export function moduleFrom(location = window.location) {
  const search = String(location.search || '');
  const id = new URLSearchParams(search).get('module');
  return id && /^[a-z0-9][a-z0-9-]{0,63}$/.test(id) ? id : null;
}

/**
 * Where a leased trainee lands: the module they asked for, on its video page
 * (watch, then Practice; spec 53 15.5), or the list.
 */
export function landingFor(location = window.location) {
  const id = moduleFrom(location);
  return id ? `${LANDING_PATH}/${id}` : LANDING_PATH;
}

/** Say something on the boot screen index.html is already showing. */
function showMessage(label, detail = '') {
  const boot = document.querySelector('.app-boot');
  if (!boot) return;
  const labelEl = boot.querySelector('.app-boot__label');
  const detailEl = boot.querySelector('.app-boot__detail');
  if (labelEl) labelEl.textContent = label;
  if (detailEl) detailEl.textContent = detail;
}

/**
 * A training link starts afresh (spec 53 15): the walkthrough this tab had
 * going (the cards' place, `lw:tour`, and the run under `lw:training`) is
 * forgotten, so cards from an earlier visit never resume over the module the
 * link asked for. Receipts production hasn't taken yet stay under
 * `lw:training`, to be sent again (5.3 step 5).
 */
function forgetRun(storage) {
  try {
    storage.removeItem('lw:tour');
    const raw = storage.getItem('lw:training');
    const saved = raw ? JSON.parse(raw) : null;
    if (saved && typeof saved === 'object' && !Array.isArray(saved) && 'run' in saved) {
      delete saved.run;
      if (Object.keys(saved).length) storage.setItem('lw:training', JSON.stringify(saved));
      else storage.removeItem('lw:training');
    }
  } catch (e) {
    // Storage refused: nothing was kept to resume either.
  }
}

const AGAIN = 'Go back to your Ladeway account and open training again.';

/**
 * @returns {Promise<'leased'|'invalid'|'refused'|'busy'|'offline'>}
 */
export async function startTraining({
  location = window.location,
  fetchFn = (...args) => window.fetch(...args),
  storage = window.sessionStorage,
  say = showMessage,
} = {}) {
  const ticket = ticketFrom(location);
  if (!ticket) {
    say('This training link is not complete.', AGAIN);
    return 'invalid';
  }
  say('Getting your practice fleet ready', 'Sample data, yours alone.');
  let res;
  let body = {};
  try {
    res = await fetchFn('/api/auth/demo/lease', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ training: ticket }),
    });
    body = await res.json().catch(() => ({}));
  } catch (e) {
    say(
      'The practice fleet could not be reached.',
      'Check your connection, then reload this page.'
    );
    return 'offline';
  }
  if (res.status === 200 && body.csrf_token) {
    // The SPA reads its CSRF token from sessionStorage (config/csrf.js), as
    // after the login page.
    storage.setItem('csrf_token', body.csrf_token);
    forgetRun(storage);
    location.replace(landingFor(location));
    return 'leased';
  }
  if (res.status === 503) {
    say('The practice fleet is busy for a minute.', 'Reload this page in a minute to try again.');
    return 'busy';
  }
  say(body.title || 'This training link did not work.', body.detail || AGAIN);
  return 'refused';
}
